import classnames from 'classnames';
import { useState, type FunctionComponent, type Ref } from 'react';

import { parseCurrency } from '@/core/tamagoshi/deprecated/PriceDeprecated/utils/currency';
import { DetailedPrice } from '@/core/tamagoshi/DetailedPrice/DetailedPrice';
import {
  Popover,
  PopoverLayover,
  PopoverTrigger,
} from '@/core/tamagoshi/Popover/Popover';
import { DeliveryLabel } from '@/core/tamagoshi/ProductCard/components/DeliveryLabel/DeliveryLabel';
import { Ratings } from '@/core/tamagoshi/Ratings/Ratings';
import { InfoOutlineIcon } from '@/core/tamagoshiv2/icons/system/InfoOutlineIcon';
import {
  ProgrammaticAdviceRecoLevel,
  type ProgrammaticAdviceRecoProduct,
} from '@/digitalAdvice/Recommendation/ProgrammaticAdviceReco.model';
import { Image } from '@/domains/core/Image/Image';
import { Link } from '@/domains/core/routing/components/Link';
import { routes } from '@/domains/core/routing/routes/routes.default';
import { createURL } from '@/domains/core/routing/URLParser';
import { isB2B } from '@/domains/core/settings/utils';
import { Tracking } from '@/domains/digitalAdvice/Recommendation/tracking';
import { translations as productCardTranslations } from '@/domains/productDiscovery/ProductCard/translations';

import {
  descriptionBasic,
  descriptionRecommended,
  descriptionStandard,
  ratingsCount,
  titleBasic,
  titleRecommended,
  titleStandard,
} from './translations.default';

import styles from './ProgrammaticAdviceRecoItem.module.scss';

const LEVEL_TO_LOCALE: Record<
  ProgrammaticAdviceRecoLevel,
  [title: string, description: string]
> = {
  [ProgrammaticAdviceRecoLevel.BASIC]: [titleBasic, descriptionBasic],
  [ProgrammaticAdviceRecoLevel.RECOMMENDED]: [
    titleRecommended,
    descriptionRecommended,
  ],
  [ProgrammaticAdviceRecoLevel.STANDARD]: [titleStandard, descriptionStandard],
};

export interface ProgrammaticAdviceRecoItemProps {
  categoryName?: string;
  className?: string;
  /** The popover `isFixed` property is not compatible with carousels */
  inCarousel?: boolean;
  level: ProgrammaticAdviceRecoLevel;
  product: ProgrammaticAdviceRecoProduct;
  onBodyClick: () => void;
}

export const ProgrammaticAdviceRecoItem: FunctionComponent<
  ProgrammaticAdviceRecoItemProps
> = ({
  categoryName,
  className,
  inCarousel = false,
  level,
  product,
  onBodyClick,
}) => {
  const { articleId, brand, modelId, price, rating, slug, thumbnail, title } =
    product;
  const [isTooltipVisible, setIsTooltipVisible] = useState<boolean>(false);

  const [levelTitle, levelDescription] = LEVEL_TO_LOCALE[level];

  const url = createURL(
    routes.product,
    { id: articleId, slug },
    { model_id: modelId },
  );

  const handleBodyClick = () => {
    Tracking.onClickProduct(categoryName, level, product);
    onBodyClick();
  };

  const toggleTooltip = (value: boolean) => () => {
    if (!isB2B()) {
      setIsTooltipVisible(value);
    }
  };

  return (
    <div className={classnames(styles.root, styles[level], className)}>
      <Popover
        isFixed={!inCarousel}
        isVisible={isTooltipVisible}
        onClose={toggleTooltip(false)}
        position="top"
      >
        <PopoverTrigger>
          {(event: { ref: Ref<HTMLDivElement> }) => (
            <div
              className={classnames(styles.header, styles[level])}
              onBlur={toggleTooltip(false)}
              onClick={toggleTooltip(true)}
              onFocus={toggleTooltip(true)}
              onKeyUp={toggleTooltip(true)}
              onMouseLeave={toggleTooltip(false)}
              onMouseOver={toggleTooltip(true)}
              ref={event.ref}
              role="button"
              tabIndex={0}
            >
              {levelTitle}
              {!isB2B() && <InfoOutlineIcon className={styles.icon} />}
            </div>
          )}
        </PopoverTrigger>
        <PopoverLayover>{levelDescription}</PopoverLayover>
      </Popover>
      <Link className={styles.body} href={url} onClick={handleBodyClick}>
        <div className={styles.thumbnail}>
          <Image alt={title} height={165} src={thumbnail} width={undefined} />
        </div>
        <div className={styles.brandTitleContainer}>
          {/* NOTE The extra div is needed to apply the padding and not mess */}
          {/*      up the line-clamp. */}
          {brand.logo && (
            <Image
              alt={brand.name}
              className={styles.brand}
              height={20}
              src={brand.logo}
              width={undefined}
            />
          )}
          <div className={styles.title}>{title}</div>
        </div>
        <DetailedPrice
          className={styles.price}
          currency={parseCurrency(price.currency)}
          placeholderDisabled
          retailValue={price.retail ?? undefined}
          value={price.current}
          retailAbove
        />
        <div className={styles.rating}>
          {rating.average > 0 && (
            <div className={styles.score}>{rating.average.toFixed(1)}</div>
          )}
          <Ratings
            className={styles.stars}
            count={ratingsCount.format({ count: rating.total }) as string}
            nbStars={rating.average}
            size="sm"
          />
        </div>
        {product.deliveryLabel ? (
          <div className={styles.delivery}>
            <DeliveryLabel
              translations={productCardTranslations.deliveryLabel}
              deliveryLabel={product.deliveryLabel}
              isPro={isB2B()}
              className={styles.deliveryLabel}
            />
          </div>
        ) : (
          <div className={styles.delivery} />
        )}
      </Link>
    </div>
  );
};
